import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const COLORS = ['#C9A85B', '#B4D330', '#6B9F4A', '#C66B4A', '#1A4331', '#8FB4D6'];
const PIECES = Array.from({ length: 52 }).map((_, i) => ({
  id: i,
  x: (Math.random() * 2 - 1) * 340,
  fall: 280 + Math.random() * 160,
  rot: Math.random() * 720 - 360,
  delay: Math.random() * 0.18,
  dur: 2.1 + Math.random() * 0.9,
  color: COLORS[i % COLORS.length],
  size: 6 + Math.random() * 9,
  round: Math.random() > 0.5,
}));

// Full-screen badge unlock celebration — a spring 'zoom' of the badge + a
// confetti burst, mirroring the iPhone harvest celebration.
export function BadgeCelebration({ badge, userName, onClose }) {
  useEffect(() => {
    if (!badge) return undefined;
    const t = setTimeout(onClose, 5200);
    return () => clearTimeout(t);
  }, [badge, onClose]);

  if (!badge) return null;

  const node = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{ background: 'rgba(15,45,31,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
      onClick={onClose} data-testid="badge-celebration">
      {/* Confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {PIECES.map((p) => (
          <motion.div key={p.id} className="absolute left-1/2 top-1/2"
            initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
            animate={{ x: p.x, y: [0, -60, p.fall], opacity: [0, 1, 1, 0], rotate: p.rot }}
            transition={{ duration: p.dur, delay: p.delay, ease: 'easeOut' }}
            style={{ width: p.size, height: p.size, borderRadius: p.round ? '9999px' : 2, background: p.color }} />
        ))}
      </div>

      {/* Zooming badge card */}
      <motion.div onClick={(e) => e.stopPropagation()} data-testid="badge-celebration-card"
        initial={{ scale: 0.4, opacity: 0, y: 24 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
        className="relative rounded-3xl p-8 text-center max-w-sm w-full"
        style={{ background: 'linear-gradient(160deg,#FAF6EC,#F4EEDE)', boxShadow: '0 30px 80px -20px rgba(15,45,31,0.6)' }}>
        <button onClick={onClose} data-testid="badge-celebration-close" className="absolute top-4 right-4 text-[#7A6B57] hover:text-[#1A4331]">
          <X className="w-5 h-5" />
        </button>
        <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.1 }}
          className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(201,168,91,0.18)' }}>
          <span style={{ fontSize: 52, lineHeight: 1 }}>{badge.icon}</span>
        </motion.div>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#C9A85B]">Badge unlocked</p>
        <h2 className="text-2xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{badge.title}</h2>
        {badge.description && <p className="text-sm text-[#7A6B57] mt-2 leading-relaxed">{badge.description}</p>}
        {badge.xp_reward != null && (
          <div className="inline-flex items-center gap-1.5 mt-4 rounded-full px-4 py-1.5" style={{ background: 'rgba(180,211,48,0.2)' }}>
            <span className="text-sm font-bold text-[#4E7A33]">+{badge.xp_reward} XP</span>
          </div>
        )}
        {userName && <p className="text-xs text-[#7A6B57] mt-4">Beautiful work, {userName} 🌱</p>}
      </motion.div>
    </div>
  );

  return createPortal(node, document.body);
}

export default BadgeCelebration;
